/**
 * The app-wide notification store behind `notify()`.
 *
 * Framework-free on purpose: a hook, a mutation callback or a poller that outlives its page can
 * all call it. React reads it through `useNotifications()` (useSyncExternalStore); the stack
 * (organisms/ToastViewport) and the bell (organisms/RecentActivity) render it.
 *
 * Rules (prototype 60-notify): success and info close after 5 s, warning, error and progress stay;
 * hovering or focusing the stack pauses every timer; the same id updates one notification in place
 * (a change of kind, or a fresh `notify()`, brings it back to the front and is read out again; a
 * progress tick only repaints); Recent activity keeps the last 30.
 */
import type {
  ActivityView,
  NotificationAction,
  NotificationAppearance,
  NotificationPlacement,
  NotificationAnnouncement,
  NotificationHandle,
  NotificationIcon,
  NotificationInput,
  NotificationKind,
  NotificationPatch,
  NotificationProgress,
  NotificationSnapshot,
  ToastView,
} from "./notify.types";
import {
  ACTIVITY_LIMIT,
  NOTIFICATION_MIN_REMAINING_MS,
  PROGRESS_ANNOUNCE_GAP_MS,
  announcementText,
  isNotificationKind,
  lifeOf,
  progressPercent,
  splitMessage,
} from "./notifyFormat";

interface NotificationRecord {
  id: string;
  /** The caller named it; twins are only merged for notifications without an id. */
  ownId: boolean;
  kind: NotificationKind;
  title: string;
  body: string;
  action: NotificationAction | null;
  secondaryAction: NotificationAction | null;
  appearance: NotificationAppearance;
  placement: NotificationPlacement;
  anchor: string | null;
  icon: NotificationIcon | null;
  progress: NotificationProgress | null;
  sticky?: boolean;
  duration?: number;
  log: boolean;
  onDismiss?: () => void;
  timer: ReturnType<typeof setTimeout> | null;
  /** Milliseconds left on its timer; 0 when it stays. */
  left: number;
  /** When its timer last started. */
  from: number;
  /** When it was last read out. */
  said: number;
}

interface ActivityRecord {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  action: NotificationAction | null;
  secondaryAction: NotificationAction | null;
  appearance: NotificationAppearance;
  placement: NotificationPlacement;
  anchor: string | null;
  icon: NotificationIcon | null;
  progress: NotificationProgress | null;
  at: number;
  read: boolean;
}

type Fields = Partial<Pick<NotificationRecord, "kind" | "title" | "body" | "action" | "secondaryAction" | "appearance" | "placement" | "anchor" | "icon" | "progress" | "sticky" | "duration" | "log" | "onDismiss">>;

const now = (): number => Date.now();

const state = {
  records: new Map<string, NotificationRecord>(),
  /** On screen, oldest first. */
  order: [] as string[],
  /** Newest first. */
  activity: [] as ActivityRecord[],
  /** Unread when the panel opened: they keep their "new" dot while it is open. */
  openSeen: new Set<string>(),
  activityOpen: false,
  seq: 0,
  announceSeq: 0,
  polite: null as NotificationAnnouncement | null,
  assertive: null as NotificationAnnouncement | null,
  /** Why the timers are paused: hover, focus, a hidden tab. */
  holds: new Set<string>(),
  viewports: 0,
};

const listeners = new Set<() => void>();

const onScreen = (): NotificationRecord[] =>
  state.order.map((id) => state.records.get(id)).filter((n): n is NotificationRecord => Boolean(n));

const toView = (n: Pick<NotificationRecord, "id" | "kind" | "title" | "body" | "action" | "secondaryAction" | "appearance" | "placement" | "anchor" | "icon" | "progress">): ToastView => ({
  id: n.id,
  kind: n.kind,
  title: n.title,
  body: n.body,
  action: n.action,
  secondaryAction: n.secondaryAction,
  appearance: n.appearance,
  placement: n.placement,
  anchor: n.anchor,
  icon: n.icon,
  progress: progressPercent(n.kind, n.progress),
});

const buildSnapshot = (): NotificationSnapshot => {
  const unread = state.activity.filter((entry) => !entry.read);
  return {
    toasts: onScreen().map(toView),
    activity: state.activity.map(
      (entry): ActivityView => ({ ...toView(entry), at: entry.at, isNew: !entry.read || state.openSeen.has(entry.id) }),
    ),
    unread: unread.length,
    unreadHasError: unread.some((entry) => entry.kind === "error"),
    activityOpen: state.activityOpen,
    polite: state.polite,
    assertive: state.assertive,
  };
};

let snapshot: NotificationSnapshot = buildSnapshot();

const emit = (): void => {
  snapshot = buildSnapshot();
  listeners.forEach((listener) => listener());
};

/** The fields a caller gave, normalised; absent keys stay absent so a patch changes only them. */
const pick = (input: NotificationPatch): Fields => {
  const out: Fields = {};
  if (input.kind !== undefined) out.kind = isNotificationKind(input.kind) ? input.kind : "info";
  if (input.title !== undefined) out.title = String(input.title ?? "").trim();
  if ("body" in input) out.body = input.body == null ? "" : String(input.body).trim();
  if ("action" in input) out.action = input.action && input.action.label ? input.action : null;
  if ("secondaryAction" in input) out.secondaryAction = input.secondaryAction && input.secondaryAction.label ? input.secondaryAction : null;
  if (input.appearance !== undefined) out.appearance = input.appearance === "inverse" ? "inverse" : "default";
  if (input.placement !== undefined) out.placement = input.placement === "bottom-center" ? "bottom-center" : "bottom-right";
  if ("anchor" in input) out.anchor = input.anchor ? String(input.anchor) : null;
  if ("icon" in input) out.icon = input.icon ?? null;
  if ("progress" in input) out.progress = input.progress ?? null;
  if ("sticky" in input) out.sticky = input.sticky;
  if ("duration" in input) out.duration = input.duration;
  if (input.log !== undefined) out.log = input.log !== false;
  if ("onDismiss" in input) out.onDismiss = input.onDismiss;
  return out;
};

// ─── Timers ──────────────────────────────────────────────────────────────────

const clearTimer = (n: NotificationRecord): void => {
  if (n.timer) clearTimeout(n.timer);
  n.timer = null;
};

const run = (n: NotificationRecord): void => {
  if (!n.left || state.holds.size > 0 || !state.order.includes(n.id)) return;
  clearTimer(n);
  n.from = now();
  n.timer = setTimeout(() => {
    n.timer = null;
    dismissNotification(n.id);
  }, n.left);
};

const arm = (n: NotificationRecord): void => {
  clearTimer(n);
  n.left = lifeOf(n);
  run(n);
};

const freeze = (n: NotificationRecord): void => {
  if (!n.timer) return;
  clearTimer(n);
  n.left = Math.max(NOTIFICATION_MIN_REMAINING_MS, n.left - (now() - n.from));
};

/** Pauses (on) or resumes (off) every timer, for a reason: hover, focus, a hidden tab. */
export const holdNotifications = (reason: string, on: boolean): void => {
  const had = state.holds.size > 0;
  if (on) state.holds.add(reason);
  else state.holds.delete(reason);
  const has = state.holds.size > 0;
  if (!had && has) onScreen().forEach(freeze);
  if (had && !has) onScreen().forEach(run);
};

// ─── Recent activity and live regions ────────────────────────────────────────

const record = (n: NotificationRecord, fresh: boolean): void => {
  if (!n.log) return;
  const existing = state.activity.find((entry) => entry.id === n.id);
  const isFresh = fresh || !existing;
  const entry: ActivityRecord = {
    id: n.id,
    kind: n.kind,
    title: n.title,
    body: n.body,
    action: n.action,
    secondaryAction: n.secondaryAction,
    appearance: n.appearance,
    placement: n.placement,
    anchor: n.anchor,
    icon: n.icon,
    progress: n.progress,
    at: isFresh || !existing ? now() : existing.at,
    read: isFresh || !existing ? state.activityOpen : existing.read,
  };
  if (isFresh) {
    if (state.activityOpen) state.openSeen.add(entry.id);
    state.activity = [entry, ...state.activity.filter((e) => e.id !== n.id)];
  } else {
    state.activity = state.activity.map((e) => (e.id === n.id ? entry : e));
  }
  if (state.activity.length > ACTIVITY_LIMIT) {
    const dropped = state.activity.slice(ACTIVITY_LIMIT);
    state.activity = state.activity.slice(0, ACTIVITY_LIMIT);
    dropped.forEach((e) => {
      if (!state.order.includes(e.id)) state.records.delete(e.id);
    });
  }
};

const announce = (n: NotificationRecord): void => {
  n.said = now();
  const message = { text: announcementText(n.kind, n.title, n.body), key: ++state.announceSeq };
  if (n.kind === "error") state.assertive = message;
  else state.polite = message;
};

// ─── API ─────────────────────────────────────────────────────────────────────

const NO_HANDLE: NotificationHandle = { id: null, update: () => NO_HANDLE, dismiss: () => undefined };

const handleFor = (id: string): NotificationHandle => ({
  id,
  update: (patch) => upsert(id, patch ?? {}, false),
  dismiss: () => dismissNotification(id),
});

function upsert(id: string, patch: NotificationPatch, viaNotify: boolean): NotificationHandle {
  const n = state.records.get(id);
  if (!n) return patch.title ? notify({ ...patch, title: patch.title, id }) : NO_HANDLE;
  const fields = pick(patch);
  if (fields.title === "") delete fields.title;
  const was = n.kind;
  Object.assign(n, fields);
  const fresh = n.kind !== was || (viaNotify && n.kind !== "progress");
  if (!state.order.includes(id) && !fresh) {
    record(n, false);
    emit();
    return handleFor(id);
  }
  if (fresh) state.order = state.order.filter((x) => x !== id).concat(id);
  record(n, fresh);
  if (fresh || now() - n.said > PROGRESS_ANNOUNCE_GAP_MS) announce(n);
  if (fresh) arm(n);
  emit();
  return handleFor(id);
}

/** Shows a notification, or updates the one with the same id. Returns a handle to update or dismiss it. */
export function notify(input: NotificationInput | string): NotificationHandle {
  const o: NotificationInput = typeof input === "string" ? { title: input } : { ...input };
  const id = o.id !== undefined && o.id !== null && o.id !== "" ? String(o.id) : null;
  if (id && state.records.has(id)) return upsert(id, o, true);
  const fields = pick(o);
  const kind = fields.kind ?? "info";
  const title = fields.title ?? "";
  if (!title) return NO_HANDLE;
  const body = fields.body ?? "";
  if (!id) {
    // The same message twice ("Link copied", "Link copied") refreshes the one on screen.
    const twin = onScreen().find((n) => !n.ownId && n.kind === kind && n.title === title && n.body === body);
    if (twin) return upsert(twin.id, o, true);
  }
  const n: NotificationRecord = {
    id: id ?? `notification-${++state.seq}`,
    ownId: Boolean(id),
    kind,
    title,
    body,
    action: fields.action ?? null,
    secondaryAction: fields.secondaryAction ?? null,
    appearance: fields.appearance ?? "default",
    placement: fields.placement ?? "bottom-right",
    anchor: fields.anchor ?? null,
    icon: fields.icon ?? null,
    progress: fields.progress ?? null,
    sticky: fields.sticky,
    duration: fields.duration,
    log: fields.log ?? true,
    onDismiss: fields.onDismiss,
    timer: null,
    left: 0,
    from: 0,
    said: 0,
  };
  state.records.set(n.id, n);
  state.order.push(n.id);
  record(n, true);
  announce(n);
  arm(n);
  emit();
  return handleFor(n.id);
}

/** Changes a notification in place. A progress tick repaints quietly; a new kind is a new event. */
export const updateNotification = (id: string, patch: NotificationPatch): NotificationHandle =>
  upsert(String(id), patch ?? {}, false);

/** Takes it off the screen (it stays in Recent activity) and calls its `onDismiss` once. */
export function dismissNotification(id: string): void {
  const n = state.records.get(String(id));
  if (!n || !state.order.includes(n.id)) return;
  clearTimer(n);
  state.order = state.order.filter((x) => x !== n.id);
  if (!n.log) state.records.delete(n.id);
  const done = n.onDismiss;
  n.onDismiss = undefined;
  emit();
  done?.();
}

/** Dismisses everything on screen ("Dismiss all"). */
export function clearNotifications(): void {
  const callbacks: (() => void)[] = [];
  onScreen().forEach((n) => {
    clearTimer(n);
    if (!n.log) state.records.delete(n.id);
    if (n.onDismiss) callbacks.push(n.onDismiss);
    n.onDismiss = undefined;
  });
  state.order = [];
  emit();
  callbacks.forEach((callback) => callback());
}

/** A plain-string message as an info (or `kind`) notification: "Draft saved. Resume it any time." */
export const toast = (message: string, kind: NotificationKind = "info", extra: Omit<NotificationInput, "title" | "body" | "kind"> = {}): NotificationHandle => {
  const text = String(message ?? "").trim();
  return text ? notify({ ...extra, kind, ...splitMessage(text) }) : NO_HANDLE;
};

/** The bell's panel opened: everything is read now, and what was unread keeps its dot while it is open. */
export const openActivity = (): void => {
  state.openSeen = new Set(state.activity.filter((entry) => !entry.read).map((entry) => entry.id));
  state.activity = state.activity.map((entry) => (entry.read ? entry : { ...entry, read: true }));
  state.activityOpen = true;
  emit();
};

export const closeActivity = (): void => {
  if (!state.activityOpen) return;
  state.activityOpen = false;
  emit();
};

/** Empties Recent activity ("Clear"); what is on screen stays. */
export const clearActivity = (): void => {
  state.activity = [];
  state.openSeen = new Set();
  Array.from(state.records.keys()).forEach((id) => {
    if (!state.order.includes(id)) state.records.delete(id);
  });
  emit();
};

/** The stack mounts once; plain `<Toast>` callers forward into it while it is there. */
export const registerNotificationViewport = (): (() => void) => {
  state.viewports += 1;
  return () => {
    state.viewports = Math.max(0, state.viewports - 1);
  };
};

export const hasNotificationViewport = (): boolean => state.viewports > 0;

export const subscribeNotifications = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getNotificationSnapshot = (): NotificationSnapshot => snapshot;

/** Tests and stories: forget everything, stop every timer. */
export const resetNotifications = (): void => {
  state.records.forEach(clearTimer);
  state.records.clear();
  state.order = [];
  state.activity = [];
  state.openSeen = new Set();
  state.activityOpen = false;
  state.polite = null;
  state.assertive = null;
  state.holds.clear();
  emit();
};

/** `notify.update(id, patch)`, `notify.dismiss(id)`, `notify.clear()`: the prototype's shape. */
notify.update = updateNotification;
notify.dismiss = dismissNotification;
notify.clear = clearNotifications;
